/**
 * Minimal, dependency-free Server-Sent Events (SSE) parsing utilities used by
 * the streaming providers. Works against a WHATWG `ReadableStream<Uint8Array>`
 * (the body returned by `fetch`).
 */

export interface SseEvent {
  /** The `event:` field, if present. */
  readonly event?: string;
  /** The concatenated `data:` payload for this event. */
  readonly data: string;
}

/**
 * Decode a byte stream into discrete SSE events.
 *
 * Handles multi-line `data:` fields, CRLF/LF newlines, and partial chunks that
 * split an event across read boundaries. Comment lines (`:`-prefixed) and the
 * `id:`/`retry:` fields are ignored, which is sufficient for the chat APIs.
 */
export async function* parseSseStream(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<SseEvent, void, unknown> {
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Emit every complete event currently buffered (LF or CRLF framed).
      let separatorIndex = indexOfEventSeparator(buffer);
      while (separatorIndex !== -1) {
        const rawEvent = buffer.slice(0, separatorIndex.index);
        buffer = buffer.slice(separatorIndex.index + separatorIndex.length);

        const parsed = parseEventBlock(rawEvent);
        if (parsed) yield parsed;

        separatorIndex = indexOfEventSeparator(buffer);
      }
    }

    // Flush any trailing event that wasn't terminated by a blank line.
    buffer += decoder.decode();
    const trailing = parseEventBlock(buffer);
    if (trailing) yield trailing;
  } finally {
    reader.releaseLock();
  }
}

interface SeparatorMatch {
  readonly index: number;
  readonly length: number;
}

/** Find the next blank-line separator (`\n\n` or `\r\n\r\n`). */
function indexOfEventSeparator(buffer: string): SeparatorMatch | -1 {
  const lf = buffer.indexOf('\n\n');
  const crlf = buffer.indexOf('\r\n\r\n');

  if (lf === -1 && crlf === -1) return -1;
  if (crlf === -1) return { index: lf, length: 2 };
  if (lf === -1) return { index: crlf, length: 4 };

  return lf < crlf ? { index: lf, length: 2 } : { index: crlf, length: 4 };
}

/** Parse a single raw event block into an {@link SseEvent}, or `null` if empty. */
function parseEventBlock(rawEvent: string): SseEvent | null {
  const lines = rawEvent.split(/\r?\n/);
  const dataParts: string[] = [];
  let eventName: string | undefined;

  for (const line of lines) {
    if (line === '' || line.startsWith(':')) continue;

    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    // Per the SSE spec a single leading space after the colon is stripped.
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);

    if (field === 'data') {
      dataParts.push(value);
    } else if (field === 'event') {
      eventName = value;
    }
    // `id` and `retry` are intentionally ignored.
  }

  if (dataParts.length === 0 && eventName === undefined) return null;

  return { event: eventName, data: dataParts.join('\n') };
}
